import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, CircularProgress } from "@mui/material";
import BranchesSection from "components/features/profile/BranchesSection";
import PracticeInformationSection from "components/features/profile/PracticeInformationSection";
import UnsavedChangesModal from "components/features/profile/UnsavedChangesModal";
import DashboardLayout from "components/layouts/DashboardLayout";
import { useToast } from "contexts/ToastContext";
import { useProfileForm } from "hooks/useProfileQueries";
import { useEffect, useState, useCallback, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  userProfileSchema,
  UserProfileFormData,
  providerSpecialtyOptions,
  ProviderSpecialtyType,
} from "validations/userSchemas";

function UserProfilePage() {
  const [currentProfileImageUrl, setCurrentProfileImageUrl] = useState<
    string | null
  >();
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );

  const navigate = useNavigate();
  const { profileData, isLoading, submitProfile, isSubmitting } =
    useProfileForm();

  const { showSuccess, showError } = useToast();
  const initialFormDataRef = useRef<UserProfileFormData | null>(null);

  const userProfileForm = useForm({
    resolver: yupResolver(userProfileSchema),
    defaultValues: {
      id: "",
      email: "",
      profileImage: null,
      firstName: "",
      middleName: "",
      lastName: "",
      phoneNumber: "",
      npi: "",
      specialties: [],
    },
    mode: "onChange",
  });

  // Function to compare form data and detect changes
  const checkForChanges = useCallback((currentData: UserProfileFormData) => {
    if (!initialFormDataRef.current) return false;

    const initial = initialFormDataRef.current;

    return (
      initial.firstName !== currentData.firstName ||
      initial.middleName !== currentData.middleName ||
      initial.lastName !== currentData.lastName ||
      initial.phoneNumber !== currentData.phoneNumber ||
      initial.npi !== currentData.npi ||
      JSON.stringify(initial.specialties) !==
        JSON.stringify(currentData.specialties) ||
      initial.profileImage !== currentData.profileImage
    );
  }, []);

  // Watch for form changes
  const watchedValues = userProfileForm.watch();

  useEffect(() => {
    if (initialFormDataRef.current) {
      const hasChanges = checkForChanges(watchedValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [watchedValues, checkForChanges]);

  // Load profile data from TanStack Query
  useEffect(() => {
    if (profileData) {
      // Filter and validate specialties from API to match form schema
      const validSpecialties = (profileData.specialty || []).filter(
        (spec: string): spec is ProviderSpecialtyType =>
          providerSpecialtyOptions.includes(spec as ProviderSpecialtyType)
      );

      const formData = {
        ...profileData,
        specialties: validSpecialties, // Map API's 'specialty' to form's 'specialties'
        profileImage: profileData.profilePicture || null, // Map API's 'profilePicture' to form's 'profileImage'
      };

      userProfileForm.reset(formData);
      initialFormDataRef.current = formData;
      setCurrentProfileImageUrl(profileData.profilePicture ?? null);
      setHasUnsavedChanges(false);
    } else {
      userProfileForm.reset({});
      initialFormDataRef.current = null;
      setCurrentProfileImageUrl(null);
      setHasUnsavedChanges(false);
    }
  }, [profileData, userProfileForm]);

  // Navigation blocking
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleNavigation = useCallback(
    (path: string) => {
      if (hasUnsavedChanges) {
        setPendingNavigation(path);
        setShowUnsavedModal(true);
      } else {
        navigate(path);
      }
    },
    [hasUnsavedChanges, navigate]
  );

  const handleSaveAndGo = async () => {
    try {
      const currentData = userProfileForm.getValues();
      await submitProfile(currentData, currentProfileImageUrl ?? null);

      showSuccess(
        "Profile Updated",
        "Your profile has been updated successfully."
      );

      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);

      if (pendingNavigation) {
        navigate(pendingNavigation);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to update profile. Please try again.";

      showError("Update Failed", errorMessage);
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  const onSubmit = async (data: UserProfileFormData) => {
    try {
      await submitProfile(data, currentProfileImageUrl ?? null);

      showSuccess(
        "Profile Updated",
        "Your profile has been updated successfully."
      );

      // Update initial form data and reset unsaved changes
      initialFormDataRef.current = data;
      setHasUnsavedChanges(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to update profile. Please try again.";

      showError("Update Failed", errorMessage);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Profile">
        <Box className="flex justify-center items-center h-64">
          <CircularProgress size={48} />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Profile" onNavigate={handleNavigation}>
      <FormProvider {...userProfileForm}>
        <form
          onSubmit={userProfileForm.handleSubmit(onSubmit)}
          className="relative"
        >
          <PracticeInformationSection
            profileImage={profileData?.profilePicture ?? ""}
          />
          <BranchesSection branches={profileData?.branches ?? []} />
          <Box
            className="flex justify-center gap-4 mt-8"
            sx={{ position: "relative", zIndex: 10 }}
          >
            <Button
              type="submit"
              variant="contained"
              className="px-6 py-2 rounded-full w-full"
              disabled={isSubmitting || !hasUnsavedChanges}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              className="px-6 py-2 rounded-full w-full"
              onClick={() => {
                userProfileForm.reset();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </FormProvider>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onCancel={handleCancelNavigation}
        onSaveAndGo={handleSaveAndGo}
        isSubmitting={isSubmitting}
      />
    </DashboardLayout>
  );
}

export default UserProfilePage;
